/**
 * CountBadge — the ONE badge shape (Peace Design Language v1, DL5).
 *
 * The ruled shape is the gold CIRCLE: "gold circle = count of updates"
 * (gold = waiting-on-you in the working register). Before this primitive
 * the family had drifted three ways — Home's task badges were gold
 * circles (rounded-full), the Kith AttentionBand count tile was a gold
 * SQUARE (rounded-base), and the AI Tools drafts count a gold pill with
 * its own fg. One primitive, one shape, one AA-tuned pair
 * (gold-soft / caution-deep — the Chip `caution` pair).
 *
 * A multi-digit count stretches the circle into a stadium (min-w + px on
 * rounded-pill) rather than shrinking the digits.
 *
 * The pepper variant carries the kickback "!" glyph (AttentionBand):
 * same shape slot, risk family — never gold, never a number-shaped
 * meaning collision.
 */

import { type ComponentPropsWithRef, type ReactNode } from 'react';

export type CountBadgeVariant = 'gold' | 'pepper';
export type CountBadgeSize = 'sm' | 'md' | 'lg';

const VARIANT_CLASSES: Record<CountBadgeVariant, string> = {
  gold: 'bg-gold-soft text-caution-deep',
  pepper: 'bg-pepper-soft text-pepper',
};

const SIZE_CLASSES: Record<CountBadgeSize, string> = {
  sm: 'h-5 min-w-5 px-1 text-[10px] font-semibold',
  md: 'h-6 min-w-6 px-1.5 text-[11px] font-semibold',
  lg: 'h-[34px] min-w-[34px] px-2 text-[15px] font-bold',
};

export interface CountBadgeProps extends ComponentPropsWithRef<'span'> {
  variant?: CountBadgeVariant;
  size?: CountBadgeSize;
}

export function CountBadge({
  variant = 'gold',
  size = 'md',
  className,
  children,
  ...rest
}: CountBadgeProps): ReactNode {
  return (
    <span
      className={[
        'inline-flex shrink-0 items-center justify-center rounded-pill tabular-nums',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </span>
  );
}
